import { useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import Button from "../../components/Button";
import DataTable, { type Column } from "../../components/DataTable";
import Badge from "../../components/Badge";
import Select from "../../components/Select";
import { usePublicationRequests } from "../../../hooks/usePublicationRequests";
import { useApprovePublicationRequest } from "../../../hooks/useApprovePublicationRequest";
import { useRejectPublicationRequest } from "../../../hooks/useRejectPublicationRequest";
import { useDeletePublicationRequest } from "../../../hooks/useDeletePublicationRequest";
import type {
  PublicationRequestResponse,
  PublicationRequestStatus,
} from "../../../api/publicationRequestsApi";

export default function PublicationRequestsPage() {
  const [filter, setFilter] = useState<PublicationRequestStatus | "">("PENDING");
  const list = usePublicationRequests(filter || undefined);
  const approve = useApprovePublicationRequest();
  const reject = useRejectPublicationRequest();
  const remove = useDeletePublicationRequest();

  const columns: Column<PublicationRequestResponse>[] = [
    {
      key: "name",
      header: "Demandeur",
      render: (row) => (
        <div>
          <div style={{ fontWeight: 600, color: "var(--ink)" }}>{row.name}</div>
          <div style={{ fontSize: 11, color: "var(--ink-faint)" }}>{row.email}</div>
        </div>
      ),
    },
    { key: "commune", header: "Commune", render: (row) => row.commune ?? "—" },
    { key: "date", header: "Date pressentie", render: (row) => row.proposedDate ?? "—" },
    {
      key: "desc",
      header: "Description",
      render: (row) => (
        <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", maxWidth: 320 }}>
          {row.description ?? "—"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Statut",
      render: (row) => (
        <Badge tone={statusToTone(row.status)}>{statusLabel(row.status)}</Badge>
      ),
    },
    {
      key: "created",
      header: "Reçu le",
      render: (row) => new Date(row.createdAt).toLocaleDateString("fr-BE"),
    },
    {
      key: "actions",
      header: "",
      render: (row) => (
        <div className="bo-row-end">
          {row.status === "PENDING" && (
            <>
              <Button
                variant="primary"
                onClick={() => approve.mutate(row.id)}
                loading={approve.isPending && approve.variables === row.id}
              >
                Valider & créer le compte
              </Button>
              <Button
                variant="ghost"
                onClick={() => window.confirm("Refuser cette demande ?") && reject.mutate(row.id)}
              >
                Refuser
              </Button>
            </>
          )}
          {row.status === "APPROVED" && row.createdParcoursId && (
            <Link to={`/admin/parcours/${row.createdParcoursId}`} className="bo-btn bo-btn--ghost">
              Voir le parcours
            </Link>
          )}
          {row.status !== "PENDING" && (
            <Button
              variant="ghost"
              onClick={() => window.confirm("Supprimer définitivement cette demande ?") && remove.mutate(row.id)}
            >
              Supprimer
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Demandes de publication"
        description="Validez les demandes envoyées via le formulaire public. La validation crée un compte éditeur et un parcours pré-rempli automatiquement."
      />
      <div className="bo-toolbar">
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value as PublicationRequestStatus | "")}
          className="bo-search"
        >
          <option value="PENDING">En attente</option>
          <option value="APPROVED">Validées</option>
          <option value="REJECTED">Refusées</option>
          <option value="">Toutes</option>
        </Select>
      </div>
      <DataTable
        rows={list.data}
        columns={columns}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyMessage="Aucune demande pour ce filtre."
      />
      {approve.isError && (
        <p className="bo-error-text">Échec de la validation : {(approve.error as Error)?.message}</p>
      )}
    </div>
  );
}

function statusToTone(s: PublicationRequestStatus): "warning" | "success" | "danger" {
  if (s === "APPROVED") return "success";
  if (s === "REJECTED") return "danger";
  return "warning";
}

function statusLabel(s: PublicationRequestStatus): string {
  if (s === "APPROVED") return "Validée";
  if (s === "REJECTED") return "Refusée";
  return "En attente";
}
